// Status system removed - reports are simple read-only entities

// Chart Types
export type ChartType = 'PIE' | 'DONUT' | 'BAR' | 'HORIZONTAL_BAR' | 'LINE';

// Chart Data Structure (compatible with Recharts)
export interface ChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor?: string[];
    borderColor?: string[];
  }>;
}

// Chart Statistics
export interface ChartStatistics {
  totalAnswers: number;
  responseRate: number;
  mostCommonAnswer?: string;
  averageRating?: number;
  distribution?: Record<string, number>;
}

// Question Report Data
export interface QuestionReportData {
  questionId: string;
  questionTitle: Record<string, string>;
  questionType: string;
  order: number;
  chartConfig?: {
    chartType: ChartType;
    data: ChartData;
    statistics: ChartStatistics;
  };
  includedComments: Array<{
    responseId: string;
    comment: string;
    timestamp: string;
    language?: string;
    colorCode?: string;
    answerValue?: string | number | boolean | string[];
  }>;
  rawDataSummary?: {
    totalAnswers: number;
    responseRate: number;
    sampleAnswers?: Array<{
      responseId: string;
      value: string | number;
      timestamp: string;
    }>;
  };
}

// Response Mapping for Interactive Features
export interface ResponseMapping {
  responseId: string;
  sessionId: string;
  colorCode?: string;
  answers: Record<
    string,
    {
      questionId: string;
      value: string | number | boolean | string[];
      hasComment: boolean;
    }
  >;
}

// PDF Export Data
export interface PdfExportData {
  exportId: string;
  exportedBy: string;
  exportedAt: string;
  fileUrl: string;
  fileName: string;
  fileSize?: number;
  // status removed - PDFs exist or they don't
  downloadUrl?: string;
}

// Main Survey Report DTO
export interface SurveyReportDTO {
  id: string;
  surveyInstanceId: string;
  surveyId: string;
  auditId: string;
  tenantId: string;
  domainId: string;
  subjectId: string;
  generatedBy: string;
  title: string;
  description?: string;
  totalResponses: number;
  includedResponses: number;
  excludedResponses: number;
  questionReports: QuestionReportData[];
  responseMapping: Record<string, ResponseMapping>;
  generatedAt: string;
  // publishedAt and isLocked removed - no status system
  pdfExports: PdfExportData[];
  createdAt: string;
  updatedAt: string;
}

// Create Survey Report DTO
export interface CreateSurveyReportDTO {
  auditId: string;
  generatedBy: string;
  title?: string;
  description?: string;
}

// Update Survey Report DTO
export interface UpdateSurveyReportDTO {
  title?: string;
  description?: string;
}

// Survey Report List DTO
export interface SurveyReportListDTO {
  reports: Array<{
    id: string;
    surveyInstanceId: string;
    surveyId: string;
    auditId: string;
    surveyTitle?: string;
    // status removed - all reports are available
    title: string;
    totalResponses: number;
    includedResponses: number;
    generatedAt: string | undefined;
    // publishedAt removed - no publish workflow
    generatedBy: string;
    pdfExports: number;
    // Additional fields for tenant-wide view
    subjectId?: string;
    subjectName?: string;
    instituteName?: string;
    createdBy?: string;
  }>;
  totalCount: number;
}

// Generate Report From Audit DTO
export interface GenerateReportFromAuditDTO {
  auditId: string;
  generatedBy: string;
  title?: string;
  description?: string;
  options?: {
    includeRawData?: boolean;
    chartType?: ChartType;
    colorScheme?: 'default' | 'accessibility' | 'custom';
  };
}

// GenerateReportResponseDTO and PublishReportDTO removed - reports generate synchronously

// Report Statistics DTO
export interface ReportStatisticsDTO {
  reportId: string;
  statistics: {
    totalQuestions: number;
    questionsWithCharts: number;
    totalResponses: number;
    includedResponses: number;
    excludedResponses: number;
    averageResponseRate: number;
    questionsWithComments: number;
    totalComments: number;
  };
  generationTime: number;
  lastUpdated: string;
}

// PDF Export DTO
export interface PdfExportDTO {
  exportId: string;
  reportId: string;
  exportedBy: string;
  exportedAt: string;
  fileUrl: string;
  fileName: string;
  fileSize?: number;
  // status removed from PDF exports
  downloadUrl?: string;
}

// PDF Export List DTO (matches server PdfExportListDTO)
export interface PdfExportListDTO {
  exports: PdfExportDTO[];
  totalCount: number;
}

// Generate PDF Export DTO (reportId comes from path param, exportedBy from auth)
export interface GeneratePdfExportDTO {
  options?: {
    includeComments?: boolean;
    includeStatistics?: boolean;
    format?: 'A4' | 'LETTER';
    orientation?: 'portrait' | 'landscape';
    includeRawData?: boolean;
  };
}

// Report Permissions DTO
export interface ReportPermissionsDTO {
  reportId: string;
  userId: string;
  permissions: {
    canView: boolean;
    canExport: boolean;
    // canPublish removed - no publish workflow
    canDelete: boolean;
    reason?: string;
  };
}

// Report Preview DTO
export interface ReportPreviewDTO {
  id: string;
  title: string;
  description?: string;
  totalResponses: number;
  includedResponses: number;
  questionCount: number;
  questionsWithCharts: number;
  totalComments: number;
  generatedAt: string;
  // canPublish removed - no publish workflow
  previewUrl?: string;
}

// Hook Options
export interface UseSurveyReportOptions {
  enabled?: boolean;
  refetchInterval?: number;
  staleTime?: number;
}

// Question Type Mappings
export const QUESTION_TYPE_LABELS = {
  YES_NO: 'Yes/No',
  SINGLE_SELECT: 'Single Choice',
  MULTI_SELECT: 'Multiple Choice',
  TEXT: 'Text Response',
  NUMBER: 'Number',
  RANGE: 'Rating Scale',
  DATE: 'Date',
  TIME: 'Time',
} as const;

// Table data types for DataManagementTab (matches SurveyReportListDTO reports array)
export interface SurveyReportTableData {
  id: string;
  surveyInstanceId: string;
  surveyId: string;
  auditId: string;
  surveyTitle?: string;
  // status removed - all reports are available
  title: string;
  totalResponses: number;
  includedResponses: number;
  generatedAt: string | undefined;
  // publishedAt removed - no publish workflow
  generatedBy: string;
  pdfExports: number; // Count of PDF exports
  // Additional fields for tenant-wide view
  subjectId?: string;
  subjectName?: string;
  instituteName?: string;
  createdBy?: string;
}

// Form data types for create/edit
export interface SurveyReportFormData {
  title: string;
  description?: string;
}
